<script lang="ts" setup>
import { computed } from 'vue';

interface Props {
  error?: string;
  errors?: string[];
  label?: string;
  name?: string;
  hint?: string;
  isDarkBackground?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  error: undefined,
  errors: () => [],
  label: undefined,
  name: undefined,
  hint: undefined,
  isDarkBackground: false,
});

const fieldErrors = computed(() => {
  return props.error ? props.errors.concat(props.error) : props.errors;
});
</script>

<template>
  <div class="ui-field">
    <label
      v-if="label"
      :for="name"
      class="ui-label"
      :class="{ 'is-dark-background': props.isDarkBackground }"
    >
      {{ label }}
    </label>
    <div class="ui-control">
      <slot :field-errors="fieldErrors" />
      <p v-if="hint" class="help">{{ hint }}</p>
      <p v-for="(errorMsg, i) in fieldErrors" :key="i" class="help is-danger">
        {{ errorMsg }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ui-label {
  flex-grow: 1;
  // min-height, not height: a fixed 20px made any label that wraps render its
  // second line *behind* the control below it (e.g. a long label in a narrow
  // grid column). Single-line labels are unchanged.
  min-height: 20px;
  font-style: normal;
  line-height: var(--line-height-20);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  margin-bottom: var(--space-6);
  display: block;
}

.ui-label.is-dark-background {
  color: #fff;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-14);
}

.help {
  font-size: var(--font-size-14);
  margin-top: var(--space-4);
}

.help.is-danger {
  color: var(--color-error-600);
}
</style>
